<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- 背景遮罩 -->
      <div 
        v-if="show"
        class="fixed inset-0 transition-opacity"
        @click="closeModal"
      >
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- 模态框内容 -->
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 w-[90vw] max-w-[90vw] mx-auto">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-lg font-medium text-gray-900">
              订单数据导入
            </h3>
            <p class="mt-1 text-sm text-gray-500">
              支持Excel(.xlsx)和CSV格式文件，批量导入订单信息
            </p>
          </div>
          <button
            @click="closeModal"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- 导入结果显示 -->
        <div v-if="importResult" class="mb-6">
          <div v-if="importResult.success" class="bg-green-50 border border-green-200 rounded-lg p-4">
            <div class="flex items-center">
              <CheckCircleIcon class="h-5 w-5 text-green-600 mr-2" />
              <h4 class="text-sm font-medium text-green-900">导入完成</h4>
            </div>
            <div class="mt-3 grid grid-cols-3 gap-4 text-center text-sm">
              <div>
                <div class="text-lg font-semibold text-green-600">{{ importResult.data.summary.success_count }}</div>
                <div class="text-green-700">成功导入</div>
              </div>
              <div>
                <div class="text-lg font-semibold text-yellow-600">{{ importResult.data.summary.skip_count || 0 }}</div>
                <div class="text-yellow-700">跳过重复</div>
              </div>
          <div>
                <div class="text-lg font-semibold text-red-600">{{ importResult.data.summary.error_count || 0 }}</div>
                <div class="text-red-700">导入失败</div>
              </div>
            </div>
            <!-- 显示错误详情 -->
            <div v-if="importResult.data.errors && importResult.data.errors.length > 0" class="mt-3">
              <details class="text-xs">
                <summary class="cursor-pointer text-red-600 hover:text-red-700">查看错误详情 ({{ importResult.data.errors.length }})</summary>
                <div class="mt-2 max-h-40 overflow-y-auto bg-white p-2 rounded border border-red-200">
                  <ul class="list-disc list-inside space-y-1 text-red-600">
                    <li v-for="(error, index) in importResult.data.errors" :key="index">{{ error }}</li>
                  </ul>
                </div>
              </details>
            </div>
          </div>

          <div v-else class="bg-red-50 border border-red-200 rounded-lg p-4">
            <div class="flex items-center">
              <ExclamationTriangleIcon class="h-5 w-5 text-red-600 mr-2" />
              <h4 class="text-sm font-medium text-red-900">导入失败</h4>
            </div>
            <div class="mt-2 text-sm text-red-700">
              {{ importResult.message || '导入过程中发生错误' }}
            </div>
          </div>
        </div>

        <!-- 模板下载 -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-3">下载导入模板</label>
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p class="text-sm text-blue-700 mb-3">
              建议先下载模板文件，按照模板格式准备数据后再进行导入
            </p>
            <p class="text-xs text-blue-600 mb-3">
              💡 提示：单次导入限制为 5,000 条数据，请合理规划导入批次
            </p>
            <div class="flex space-x-3">
              <button 
                @click="downloadTemplate('excel')" 
                :disabled="isDownloadingTemplate"
                class="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 disabled:opacity-50 transition-colors"
              >
                <DocumentArrowDownIcon class="h-4 w-4 mr-2" />
                Excel模板
              </button>
              <button 
                @click="downloadTemplate('csv')" 
                :disabled="isDownloadingTemplate"
                class="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-lg hover:bg-blue-50 disabled:opacity-50 transition-colors"
              >
                <DocumentArrowDownIcon class="h-4 w-4 mr-2" />
                CSV模板
              </button>
            </div>
          </div>
        </div>

        <!-- 文件上传 -->
        <div v-if="!selectedFile" class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-3">选择导入文件</label>
          
          <div class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
            <DocumentIcon class="mx-auto h-8 w-8 text-gray-400 mb-2" />
            <div class="text-sm text-gray-600">
              <label class="cursor-pointer text-blue-600 hover:text-blue-500 font-medium">
                选择文件
              <input
                  type="file" 
                ref="fileInput"
                  accept=".csv,.xlsx,.xls"
                @change="handleFileSelect"
                  class="sr-only" 
                />
              </label>
              或拖拽文件到此处
            </div>
            <p class="text-xs text-gray-500 mt-1">支持CSV, XLSX格式</p>
          </div>
        </div>

        <!-- 选中的文件信息（只在图片处理完成后显示） -->
        <div v-if="selectedFile && (detectedEmbeddedImages.length === 0 || imagesUploaded)" class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-3">已选择文件</label>
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <DocumentIcon class="h-4 w-4 text-gray-500 mr-2" />
                <span class="text-sm text-gray-900">{{ selectedFile.name }}</span>
                <span class="text-xs text-gray-500 ml-2">({{ formatFileSize(selectedFile.size) }})</span>
              </div>
              <button @click="removeFile" class="text-gray-400 hover:text-red-500 transition-colors">
                <XMarkIcon class="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <!-- 图片处理状态区域 -->
        <div v-if="extractingImages || uploadingImages || (detectedEmbeddedImages.length > 0 && !imagesUploaded) || imagesUploaded" class="mb-6">
          <!-- 正在提取图片 -->
          <div v-if="extractingImages" class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div class="flex items-center">
              <svg class="animate-spin h-5 w-5 text-blue-600 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <h4 class="text-sm font-medium text-blue-900">正在提取图片...</h4>
            </div>
          </div>

          <!-- 正在上传图片 -->
          <div v-else-if="uploadingImages" class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div class="flex items-center mb-3">
              <svg class="animate-spin h-5 w-5 text-blue-600 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <h4 class="text-sm font-medium text-blue-900">正在上传图片...</h4>
            </div>
            <div class="space-y-2">
              <div class="flex justify-between text-sm text-blue-700">
                <span>已上传 {{ imageUploadProgress.current }} / {{ imageUploadProgress.total }} 张图片</span>
                <span>{{ imageUploadProgress.percent }}%</span>
              </div>
              <div class="w-full bg-blue-200 rounded-full h-2">
                <div 
                  class="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  :style="{ width: imageUploadProgress.percent + '%' }"
                ></div>
              </div>
              <div v-if="imageUploadProgress.failed > 0" class="text-xs text-red-600">
                失败: {{ imageUploadProgress.failed }} 张
              </div>
            </div>
          </div>

          <!-- 检测到的嵌入图片 - 待确认 -->
          <div v-else-if="detectedEmbeddedImages.length > 0 && !imagesUploaded" class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div class="flex items-start">
              <ExclamationTriangleIcon class="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
              <div class="flex-1">
                <h4 class="text-sm font-medium text-yellow-900 mb-2">
                  检测到 {{ detectedEmbeddedImages.length }} 张嵌入的图片
                </h4>
                <p class="text-xs text-yellow-700 mb-3">
                  是否需要上传这些图片？如果不上传，订单将不包含图片信息。
                </p>
                <div class="flex space-x-3">
                  <button
                    @click="uploadExtractedImages(detectedEmbeddedImages)"
                    class="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <svg class="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                    上传图片
                  </button>
                  <button
                    @click="skipImageUpload"
                    class="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    跳过，不上传
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- 图片已上传提示 -->
          <div v-else-if="imagesUploaded" class="bg-green-50 border border-green-200 rounded-lg p-4">
            <div class="flex items-start">
              <CheckCircleIcon class="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
              <div class="flex-1">
                <h4 class="text-sm font-medium text-green-900 mb-2">
                  已上传 {{ Object.keys(imageUrlMapping).length }} 张图片
                </h4>
                <p class="text-xs text-green-700">
                  图片已准备就绪，可以开始导入订单数据
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- 导入说明 -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-2">导入说明</label>
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <ul class="text-xs text-gray-600 space-y-1">
              <li>• 订单编号为必填项，其他字段均为可选</li>
              <li>• 日期时间格式支持：YYYY-MM-DD HH:MM:SS 或 YYYY-MM-DD</li>
              <li>• 金额字段请填写数字，不要包含货币符号</li>
              <li>• 图片可以直接插入到Excel单元格中，系统会自动提取并上传</li>
              <li>• 处理后的文件大小不能超过10MB（原始文件可包含图片，无大小限制）</li>
            </ul>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            @click="closeModal"
            type="button"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
            :disabled="isImporting || uploadingImages || extractingImages"
          >
            {{ importResult ? '关闭' : '取消' }}
          </button>
          
          <button 
            v-if="importResult"
            @click="resetImport"
            type="button"
            class="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-300 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
          >
            重新导入
          </button>
          
          <button
            v-else
            @click="startImport"
            type="button"
            :disabled="isImporting || uploadingImages || extractingImages || !selectedFile || !canStartImport"
            :title="!canStartImport && detectedEmbeddedImages.length > 0 ? '请先上传图片或选择跳过' : ''"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
          >
            <svg v-if="isImporting || uploadingImages || extractingImages" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ extractingImages ? '提取图片中...' : (uploadingImages ? '上传图片中...' : (isImporting ? '导入中...' : '开始导入')) }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useToast } from 'vue-toastification'
import { 
  XMarkIcon, 
  DocumentIcon, 
  DocumentArrowDownIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/vue/24/outline'
import orderService from '@/services/orderService'
import * as XLSX from 'xlsx'
import JSZip from 'jszip'

// Props
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['close', 'imported'])

// Composables
const toast = useToast()

// State
const selectedFile = ref(null)
const isDownloadingTemplate = ref(false)
const isImporting = ref(false)
const extractingImages = ref(false)
const uploadingImages = ref(false)
const importResult = ref(null)
const detectedEmbeddedImages = ref([])
const parsedExcelData = ref(null)
const imageUrlMapping = ref({})  // 图片索引 -> MinIO URL 的映射
const imagesUploaded = ref(false)  // 标记图片是否已上传

// File input ref
const fileInput = ref(null)

// 图片上传进度
const imageUploadProgress = ref({
  current: 0,
  total: 0,
  percent: 0,
  failed: 0
})

// 计算是否可以开始导入
const canStartImport = computed(() => {
  // 如果检测到图片但还没有处理（上传或跳过），则不能导入
  if (detectedEmbeddedImages.value.length > 0 && !imagesUploaded.value) {
    return false
  }
  return true
})

// Watch for modal show/hide
watch(() => props.show, (show) => {
  if (show) {
    resetState()
  }
})

// Methods
const resetState = () => {
  selectedFile.value = null
  isImporting.value = false
  uploadingImages.value = false
  extractingImages.value = false
  importResult.value = null
  detectedEmbeddedImages.value = []
  imageUrlMapping.value = {}
  parsedExcelData.value = null
  imagesUploaded.value = false
  imageUploadProgress.value = { current: 0, total: 0, percent: 0, failed: 0 }
}

const handleFileSelect = async (event) => {
  const file = event.target.files[0]
  if (!file) return
  
  // 验证文件类型
  const allowedTypes = ['.csv', '.xlsx', '.xls']
  const fileExtension = '.' + file.name.split('.').pop().toLowerCase()
  
  if (!allowedTypes.includes(fileExtension)) {
    toast.error('只支持CSV和Excel文件格式')
    return
  }
  
  selectedFile.value = file
  
  // 如果是Excel文件，提取嵌入的图片
  if (fileExtension === '.xlsx') {
    await extractAndUploadImagesFromExcel(file)
  }
}

// 从Excel中提取嵌入的图片并自动上传
const extractAndUploadImagesFromExcel = async (file) => {
  extractingImages.value = true
  
  try {
    const arrayBuffer = await file.arrayBuffer()
    const zip = await JSZip.loadAsync(arrayBuffer)
    
    // 读取Excel数据
    const workbook = XLSX.read(arrayBuffer, { type: 'array' })
    const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
    const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 })
    parsedExcelData.value = { workbook, firstSheet, jsonData }
    
    // 找到图片列的索引
    const headers = jsonData[0]
    const imageColumns = {
      orderScreenshot: headers.indexOf('订单截图URL'),
      paypalScreenshot: headers.indexOf('PayPal返款截图URL'),
      reviewScreenshot: headers.indexOf('留评截图URL')
    }
    
    // 提取xl/media/目录中的图片
    const mediaFolder = zip.folder('xl/media')
    if (!mediaFolder) {
      extractingImages.value = false
      return
    }
    
    const imageFiles = []
    const imagePromises = []
    
    mediaFolder.forEach((relativePath, file) => {
      if (!file.dir && /\.(png|jpg|jpeg|gif|bmp)$/i.test(relativePath)) {
        imagePromises.push(
          file.async('blob').then(blob => {
            const extension = relativePath.split('.').pop()
            return {
              name: relativePath,
              blob: blob,
              file: new File([blob], relativePath, { type: `image/${extension}` })
            }
          })
        )
      }
    })
    
    const extractedImages = await Promise.all(imagePromises)
    
    if (extractedImages.length > 0) {
      detectedEmbeddedImages.value = extractedImages
      toast.info(`检测到 ${extractedImages.length} 张嵌入的图片`)
      
      // 不再自动上传，等待用户确认
    }
  } catch (error) {
    console.error('提取图片失败:', error)
    toast.error('提取图片失败，将继续导入数据')
  } finally {
    extractingImages.value = false
  }
}

// 上传提取的图片（并发上传，实时更新进度）
const uploadExtractedImages = async (images) => {
  if (images.length === 0) return
  
  uploadingImages.value = true
  imageUploadProgress.value = {
    current: 0,
    total: images.length,
    percent: 0,
    failed: 0
  }
  
  try {
    let completedCount = 0
    let failedCount = 0
    const concurrencyLimit = 5 // 并发上传5张图片
    
    // 并发控制函数
    const uploadWithConcurrency = async () => {
      const results = []
      let currentIndex = 0
      
      // 创建上传任务
      const uploadTask = async (image) => {
        const result = await orderService.uploadSingleImage(image.file)
        completedCount++
        
        if (result.success) {
          // 建立图片索引到URL的映射
          imageUrlMapping.value[image.name] = result.data.url
          results.push({ success: true, url: result.data.url, name: image.name })
        } else {
          failedCount++
          results.push({ success: false, error: result.message, name: image.name })
        }
        
        // 更新进度
        imageUploadProgress.value.current = completedCount
        imageUploadProgress.value.failed = failedCount
        imageUploadProgress.value.percent = Math.round((completedCount / images.length) * 100)
        
        return result
      }
      
      // 工作器函数
      const worker = async () => {
        while (currentIndex < images.length) {
          const image = images[currentIndex++]
          await uploadTask(image)
        }
      }
      
      // 创建并发工作器
      const workers = []
      for (let i = 0; i < Math.min(concurrencyLimit, images.length); i++) {
        workers.push(worker())
      }
      
      // 等待所有工作器完成
      await Promise.all(workers)
      
      return results
    }
    
    await uploadWithConcurrency()
    
    // 上传完成提示
    const successCount = completedCount - failedCount
    if (failedCount > 0) {
      toast.warning(`上传完成！成功: ${successCount}, 失败: ${failedCount}`)
  } else {
      toast.success(`成功上传 ${successCount} 张图片`)
    }
    
    // 将图片URL写入Excel数据
    if (successCount > 0) {
      await updateExcelWithImageUrls()
    }
    
    // 标记图片已上传
    imagesUploaded.value = true
  } catch (error) {
    console.error('上传图片失败:', error)
    toast.error('上传图片失败')
  } finally {
    uploadingImages.value = false
  }
}

// 跳过图片上传，清空图片字段
const skipImageUpload = async () => {
  if (!parsedExcelData.value) {
    detectedEmbeddedImages.value = []
    imagesUploaded.value = false
    toast.info('已跳过图片上传')
    return
  }
  
  try {
    const { workbook, jsonData } = parsedExcelData.value
    const headers = jsonData[0]
    
    // 找到图片列
    const imageColumns = {
      orderScreenshot: headers.indexOf('订单截图URL'),
      paypalScreenshot: headers.indexOf('PayPal返款截图URL'),
      reviewScreenshot: headers.indexOf('留评截图URL')
    }
    
    // 清空所有图片字段
    for (let i = 1; i < jsonData.length; i++) {
      const row = jsonData[i]
      
      if (imageColumns.orderScreenshot !== -1) {
        row[imageColumns.orderScreenshot] = ''
      }
      if (imageColumns.paypalScreenshot !== -1) {
        row[imageColumns.paypalScreenshot] = ''
      }
      if (imageColumns.reviewScreenshot !== -1) {
        row[imageColumns.reviewScreenshot] = ''
      }
    }
    
    // 更新工作表
    const newSheet = XLSX.utils.aoa_to_sheet(jsonData)
    workbook.Sheets[workbook.SheetNames[0]] = newSheet
    
    // 生成新的Excel文件
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const newFile = new File([blob], selectedFile.value.name, { type: selectedFile.value.type })
    
    selectedFile.value = newFile
    
    detectedEmbeddedImages.value = []
    imagesUploaded.value = false
    
    toast.info('已清空图片字段，可以直接导入数据')
  } catch (error) {
    console.error('清空图片字段失败:', error)
    toast.error('清空图片字段失败')
  }
}

// 更新Excel数据，将图片URL写入对应单元格
const updateExcelWithImageUrls = async () => {
  if (!parsedExcelData.value || Object.keys(imageUrlMapping.value).length === 0) {
    return
  }
  
  try {
    const { workbook, jsonData } = parsedExcelData.value
    const headers = jsonData[0]
    
    // 找到图片列
    const imageColumns = {
      orderScreenshot: headers.indexOf('订单截图URL'),
      paypalScreenshot: headers.indexOf('PayPal返款截图URL'),
      reviewScreenshot: headers.indexOf('留评截图URL')
    }
    
    const urls = Object.values(imageUrlMapping.value)
    let urlIndex = 0
    
    for (let i = 1; i < jsonData.length && urlIndex < urls.length; i++) {
      const row = jsonData[i]
      
      // 填充订单截图（无论是否为空，都填充）
      if (imageColumns.orderScreenshot !== -1 && urlIndex < urls.length) {
        row[imageColumns.orderScreenshot] = urls[urlIndex++]
      }
      
      // 填充PayPal截图
      if (imageColumns.paypalScreenshot !== -1 && urlIndex < urls.length) {
        row[imageColumns.paypalScreenshot] = urls[urlIndex++]
      }
      
      // 填充留评截图
      if (imageColumns.reviewScreenshot !== -1 && urlIndex < urls.length) {
        row[imageColumns.reviewScreenshot] = urls[urlIndex++]
      }
    }
    
    // 更新工作表
    const newSheet = XLSX.utils.aoa_to_sheet(jsonData)
    workbook.Sheets[workbook.SheetNames[0]] = newSheet
    
    // 生成新的Excel文件
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const newFile = new File([blob], selectedFile.value.name, { type: selectedFile.value.type })
    
    selectedFile.value = newFile
    
    toast.success(`已将 ${urlIndex} 个图片URL填充到Excel中`)
  } catch (error) {
    console.error('更新Excel失败:', error)
    toast.error('更新Excel失败')
  }
}

const removeFile = () => {
  selectedFile.value = null
  detectedEmbeddedImages.value = []
  parsedExcelData.value = null
  imageUrlMapping.value = {}
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const downloadTemplate = async (format) => {
  if (isDownloadingTemplate.value) return
  
  isDownloadingTemplate.value = true
  
  try {
    const result = await orderService.downloadImportTemplate(format)
    
    if (result.success) {
      const url = URL.createObjectURL(result.blob)
      const a = document.createElement('a')
      a.href = url
      a.download = result.filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      
      toast.success('模板下载成功')
    } else {
      toast.error(result.message || '模板下载失败')
    }
  } catch (error) {
    console.error('模板下载失败:', error)
    toast.error('模板下载失败')
  } finally {
    isDownloadingTemplate.value = false
  }
}

const closeModal = () => {
  if (!isImporting.value && !uploadingImages.value && !extractingImages.value) {
    emit('close')
  }
}

const startImport = async () => {
  if (!selectedFile.value) {
    toast.error('请选择导入文件')
    return
  }

  // 验证文件大小 (10MB)
  if (selectedFile.value.size > 10 * 1024 * 1024) {
    toast.error('处理后的文件大小不能超过10MB，请减少数据行数或图片数量')
    return
  }

  isImporting.value = true
  importResult.value = null
  
  try {
    const formData = new FormData()
    formData.append('file', selectedFile.value)
    
    const result = await orderService.importExternalOrders(formData)
    
    if (result.success) {
      toast.success('订单导入成功!')
      emit('imported', result)
      // 导入成功后自动关闭模态框
      setTimeout(() => {
      emit('close')
      }, 1000)
    } else {
      toast.error(result.message || '导入失败')
      importResult.value = result
    }
  } catch (error) {
    console.error('导入失败:', error)
    importResult.value = {
      success: false,
      message: '导入过程中发生错误，请稍后重试'
    }
    toast.error('导入失败，请稍后重试')
  } finally {
    isImporting.value = false
  }
}

const resetImport = () => {
  importResult.value = null
  resetState()
}
</script>

<style scoped>
/* 自定义样式可以在这里添加 */
</style>

